import React, { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { getDb } from '../firestoreRegistry.js';
import { useNavigate } from 'react-router-dom';
import './Account.css';

export default function Account() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hostedCount, setHostedCount] = useState(0);
  const [guestCount, setGuestCount] = useState(0);
  const [profileData, setProfileData] = useState(null);
  
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser || currentUser.isAnonymous) {
        setLoading(false);
        setUser(null);
        return;
      }

      setUser(currentUser);

      // Fetch real stats from Firestore
      try {
        const momentsDb = getDb('moments');
        
        // Get hosted moments count
        const hostedRef = collection(momentsDb, 'moments');
        const hostedQuery = query(
          hostedRef,
          where('hostId', '==', currentUser.uid),
          orderBy('updatedAt', 'desc'),
          limit(100)
        );
        const hostedSnapshot = await getDocs(hostedQuery);
        setHostedCount(hostedSnapshot.size);

        // Get guest moments count (cohost + memberships)
        let guestTotal = 0;
        const seen = new Set();

        // Count cohost moments
        try {
          const cohostQuery = query(
            hostedRef,
            where('cohostIds', 'array-contains', currentUser.uid),
            orderBy('updatedAt', 'desc'),
            limit(100)
          );
          const cohostSnapshot = await getDocs(cohostQuery);
          cohostSnapshot.forEach((doc) => {
            if (!seen.has(doc.id)) {
              seen.add(doc.id);
              guestTotal++;
            }
          });
        } catch (err) {
          console.warn('Could not fetch cohost moments:', err);
        }

        // Count membership moments
        try {
          const membershipRef = collection(momentsDb, 'users', currentUser.uid, 'memberships');
          const membershipSnapshot = await getDocs(membershipRef);
          membershipSnapshot.forEach((doc) => {
            const data = doc.data() || {};
            const momentId = data.momentId || doc.id;
            if (momentId && !seen.has(momentId)) {
              seen.add(momentId);
              guestTotal++;
            }
          });
        } catch (err) {
          console.warn('Could not fetch memberships:', err);
        }

        setGuestCount(guestTotal);

        // Fetch profile data
        try {
          const userRef = doc(momentsDb, 'users', currentUser.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            setProfileData(userSnap.data());
          }
        } catch (err) {
          console.warn('Could not fetch profile data:', err);
        }

      } catch (error) {
        console.error('Error fetching account data:', error);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <main className="account-main" aria-label="Account Content">
        <div className="account-container">
          <p className="account-loading">Loading your account...</p>
        </div>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="account-main" aria-label="Account Content">
        <div className="account-container">
          <section className="section-header">
            <h1 className="account-title">Account</h1>
            <p className="account-subtitle">Please sign in to view your account.</p>
          </section>
        </div>
      </main>
    );
  }

  const displayName = profileData?.displayName || user.displayName || 'User';
  const email = user.email || '';
  const phone = user.phoneNumber || '';
  const photoURL = user.photoURL || '';
  const plan = profileData?.plan || 'Free';

  return (
    <main className="account-main" aria-label="Account Content">
      <div className="account-container">
        <section className="section-header" aria-label="Account Header">
          <h1 className="account-title">Account</h1>
          <p className="account-subtitle">Manage your profile and view your activity</p>
        </section>

        {/* Profile Section */}
        <section className="section-profile" aria-label="Profile Information">
            <div className="profile-card">
              <div className="profile-avatar">
              {photoURL ? (
                <img
                  src={photoURL}
                  alt={`${displayName}'s PartyDecider profile photo`}
                  className="profile-avatar-img"
                />
              ) : (
                <div className="profile-avatar-placeholder">
                  {displayName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div className="profile-info">
              <h2 className="profile-name">{displayName}</h2>
              <div className="profile-details">
                {email && (
                  <div className="profile-detail">
                    <span className="profile-detail-label">Email:</span>
                    <span className="profile-detail-value">{email}</span>
                  </div>
                )}
                {phone && (
                  <div className="profile-detail">
                    <span className="profile-detail-label">Phone:</span>
                    <span className="profile-detail-value">{phone}</span>
                  </div>
                )}
                <div className="profile-detail">
                  <span className="profile-detail-label">Plan:</span>
                  <span className="profile-detail-value profile-plan">{plan}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="section-stats" aria-label="Activity Stats">
          <h2 className="section-stats-title">Your Activity</h2>
          <div className="block-stats">
            <div className="stat">
              <div className="stat-value" data-stat="hosted">{hostedCount}</div>
              <div className="stat-label">Moments as host</div>
            </div>
            <div className="stat">
              <div className="stat-value" data-stat="joined">{guestCount}</div>
              <div className="stat-label">Moments as guest</div>
            </div>
            <div className="stat">
              <div className="stat-value" data-stat="total">{hostedCount + guestCount}</div>
              <div className="stat-label">Total moments</div>
            </div>
          </div>
          {hostedCount + guestCount === 0 ? (
            <p className="stats-message">
              🎉 Welcome! Start your journey by creating your first moment or joining an event.
            </p>
          ) : hostedCount === 0 && guestCount > 0 ? (
            <p className="stats-message">
              You've joined {guestCount} {guestCount === 1 ? 'moment' : 'moments'}! Ready to host your own?
            </p>
          ) : guestCount === 0 && hostedCount > 0 ? (
            <p className="stats-message">
              {hostedCount === 1 
                ? "You've hosted your first moment! Keep it going." 
                : `You've hosted ${hostedCount} moments! You're a natural organizer.`}
            </p>
          ) : hostedCount > guestCount ? (
            <p className="stats-message">
              You're a natural host! Keep creating amazing moments.
            </p>
          ) : guestCount > hostedCount ? (
            <p className="stats-message">
              You're a social butterfly! Consider hosting your own event.
            </p>
          ) : (
            <p className="stats-message">
              Great balance! You're both a host and a guest.
            </p>
          )}
        </section>

        {/* Quick Actions */}
        <section className="section-actions" aria-label="Quick Actions">
          <button 
            className="action-btn action-btn-primary"
            onClick={() => navigate('/planner/profile')}
          >
            View My Plans
          </button>
          <button 
            className="action-btn action-btn-secondary"
            onClick={() => navigate('/planner')}
          >
            Create New Moment
          </button>
        </section>
      </div>
    </main>
  );
}
